import { wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import { SwingSide, SwingSideGenerator } from "building-types/doors/doorTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallIndex } from "building-types/walls/wallIndex";
import { ensureDefined } from "common/utils/assertOfType";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import {
  polyLine2EdgeCount,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Start,
} from "geom/polyLine2";
import { vec2Dot, vec2Dist } from "geom/vec2";

/**
 * computeDoorSwingSide finds the wall swing direction by looking for
 * the closest solid wall.
 * */

export function computeDoorSwingSide(door: GeneratedDoorPlacement, index: WallIndex): SwingSide {
  if (!door.descriptor.requiresDoorSwing || !SwingSideGenerator[door.swingSide]) {
    return door.swingSide as SwingSide;
  }

  let frontRoom: Room = door.frontRoom;
  let halfEdgeIndex: number = door.halfEdgeIndex;

  switch (door.type) {
    case "DOOR": {
      if (door.wallStripDomain && door.reverse) {
        frontRoom = door.backRoom;
        halfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
      }
      break;
    }
    case "DOOR_V2": {
      if (door.reverse) {
        frontRoom = door.backRoom;
        halfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
      }
      break;
    }
  }

  const swingSideRight = door.descriptor.operationType === DoorOperationType.POCKET ? SwingSide.LEFT : SwingSide.RIGHT;
  const swingSideLeft = door.descriptor.operationType === DoorOperationType.POCKET ? SwingSide.RIGHT : SwingSide.LEFT;

  if (!frontRoom) {
    // this might occur if the door opens to the outside of the building
    return swingSideRight;
  }

  const edgeIndex = index.planarGraph.halfEdgeIndexToPolygonEdgeIndex(halfEdgeIndex);

  // if facing the door from the start room, the next wall is to the left, the prev wall is to the right
  const edgeCount = polyLine2EdgeCount(frontRoom.outline);
  const nextEdgeIndex = (edgeIndex + 1) % edgeCount;
  const prevEdgeIndex = (edgeIndex - 1 + edgeCount) % edgeCount;

  const strip = index.wallStripFromRoomEdgeIndex(frontRoom, edgeIndex);
  const nextStrip = index.wallStripFromRoomEdgeIndex(frontRoom, nextEdgeIndex);
  const prevStrip = index.wallStripFromRoomEdgeIndex(frontRoom, prevEdgeIndex);
  if (!strip || !nextStrip || !prevStrip) {
    return swingSideRight;
  }

  // if either wall is void, we swing away from it
  const nextIsVoid = nextStrip.firstWall().wallType === WallType.VOID;
  const prevIsVoid = prevStrip.lastWall().wallType === WallType.VOID;

  if (nextIsVoid) {
    return swingSideRight;
  } else if (prevIsVoid) {
    return swingSideLeft;
  }

  // both walls on either side of door wall are solid
  const baseLine = strip.baseline(false);
  const nextBaseLine = nextStrip.baseline(false);
  const prevBaseLine = prevStrip.baseline(false);

  // determine if the walls are directed away from the wall, or directed back
  const [normalX, normalY] = polyLine2EdgeNormal(baseLine, 0, true);
  const [nextTangentX, nextTangentY] = polyLine2EdgeTangent(nextBaseLine, 0);
  const [prevTangentX, prevTangentY] = polyLine2EdgeTangent(prevBaseLine, -1, true);

  const nextOut = vec2Dot(normalX, normalY, nextTangentX, nextTangentY) > 0;
  const prevOut = vec2Dot(normalX, normalY, prevTangentX, prevTangentY) > 0;

  // if either wall is not directed out, we swing away from it
  if (!nextOut) {
    return swingSideRight;
  } else if (!prevOut) {
    return swingSideLeft;
  }

  // if both walls are solid, we swing towards the closer wall
  const [nextX, nextY] = polyLine2Start(nextBaseLine);
  const [prevX, prevY] = polyLine2End(prevBaseLine);

  const planOutline = wallOpeningPlanExtents(ensureDefined(door.opening));

  const nextDist = vec2Dist(nextX, nextY, planOutline[0], planOutline[1]);
  const prevDist = vec2Dist(prevX, prevY, planOutline[2], planOutline[3]);

  if (nextDist < prevDist) {
    return swingSideLeft;
  }

  return swingSideRight;
}
